/* EYW CTA behaviors: info overlay, ledger drawer, next payout streak calc.
   Safe for multiple sections on a page; include this file once. */

(function(){
  // ---------- Single, shared INFO overlay ----------
  function findExistingOverlay(){ return document.querySelector('.eyw-info-overlay'); }

  let infoOverlay; // cached reference
  function ensureInfoOverlay(){
    infoOverlay = infoOverlay || findExistingOverlay();
    if (infoOverlay) return infoOverlay;

    infoOverlay = document.createElement('div');
    infoOverlay.className = 'eyw-info-overlay';

    const card = document.createElement('div');
    card.className = 'eyw-info-card';

    const text = document.createElement('div');
    text.className = 'eyw-info-text';

    const closeBtn = document.createElement('button');
    closeBtn.className = 'btn-light';
    closeBtn.textContent = 'Close';
    closeBtn.style.marginTop = '8px';
    closeBtn.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      infoOverlay.style.display = 'none';
    });

    card.appendChild(text);
    card.appendChild(closeBtn);
    infoOverlay.appendChild(card);

    // Close when tapping backdrop or pressing ESC
    infoOverlay.addEventListener('click', (e)=>{ if (e.target === infoOverlay) infoOverlay.style.display = 'none'; });
    document.addEventListener('keydown', (e)=>{ if (e.key === 'Escape') infoOverlay.style.display = 'none'; });

    document.body.appendChild(infoOverlay);
    return infoOverlay;
  }

  function showInfo(message){
    const ov = ensureInfoOverlay();
    const textEl = ov.querySelector('.eyw-info-text');
    textEl.textContent = String(message || '').trim();
    ov.style.display = 'block';
  }

  // ---------- Currency helper (accepts cents) ----------
  function formatMoneyFromCents(cents) {
    try { if (window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(cents); } catch (_) {}
    const v = Number(cents) / 100;
    return v.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  }

  // ---------- Date helpers (4 years from order, e.g., "Nov 23rd, 2029") ----------
  const MONTHS_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  function ordinal(n){ const s=["th","st","nd","rd"], v=n%100; return n+(s[(v-20)%10]||s[v]||s[0]); }
  function addYears(d, y){ const nd = new Date(d.getTime()); nd.setFullYear(nd.getFullYear()+y); return nd; }
  function formatAbbrevDate(d){ return `${MONTHS_ABBR[d.getMonth()]} ${ordinal(d.getDate())}, ${d.getFullYear()}`; }

  // For each EYW section on the page
  document.querySelectorAll('.eyw-cta').forEach((root) => {
    // Parse JSON blobs local to this section
    function parseJSONFrom(selector, fallback) {
      try {
        const node = root.querySelector(selector);
        return JSON.parse(node ? (node.textContent || '') : '') || fallback;
      } catch (_e) { return fallback; }
    }
    const enrollment = parseJSONFrom('[data-eyw-enrollment-json]', {});
    const paceline   = parseJSONFrom('[data-eyw-paceline-json]', {});
    const ledgerData = parseJSONFrom('[data-eyw-ledger-json]', []);

    // Compute 4-year deadline (fallback if Liquid didn't inject it)
    let fourYearDeadline = '';
    if (enrollment && enrollment.order_date) {
      const od = new Date(enrollment.order_date);
      if (!isNaN(od.getTime())) fourYearDeadline = formatAbbrevDate(addYears(od, 4));
    }

    // Inject the date into the Remaining Payouts info copy if missing
    const remainingInfoBtn = root.querySelector('.eyw-item .eyw-label .eyw-info');
    if (remainingInfoBtn && remainingInfoBtn.dataset && typeof remainingInfoBtn.dataset.info === 'string' && fourYearDeadline) {
      let msg = remainingInfoBtn.dataset.info;
      msg = msg
        .replace(/\{\{\s*four_years_date\s*\}\}/gi, fourYearDeadline)
        .replace(/\{FOUR_YEARS_DATE\}/g, fourYearDeadline)
        .replace(/<date>/gi, fourYearDeadline);
      if (!/Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec/i.test(msg)) {
        msg = `${msg.trim()} You must hit all remaining streaks by ${fourYearDeadline} to be eligible.`;
      }
      remainingInfoBtn.dataset.info = msg;
    }

    // Delegated clicks (info + ledger)
    root.addEventListener('click', (e) => {
      const infoBtn = e.target.closest('.eyw-info');
      if (infoBtn && infoBtn.dataset.info) {
        e.preventDefault();
        showInfo(infoBtn.dataset.info);
        return;
      }
      if (e.target.closest('[data-eyw-ledger-open]')) {
        e.preventDefault();
        openLedger(root);
        return;
      }
      if (e.target.closest('[data-eyw-ledger-close]')) {
        e.preventDefault();
        closeLedger(root);
        return;
      }
    });

    // Next Payout Streak (display = current cumulative + streaks to next)
    const nextEl = root.querySelector('[data-eyw-next-streak]');
    if (nextEl) {
      const cumulative = Number(paceline['cumulativeStreaks__streak-summary'] ?? paceline.cumulative ?? 0);
      const start   = Math.max(0, Number(enrollment.start_streak ?? 0));
      const stride  = Math.max(1, Number(enrollment.stride ?? 1));
      let toNext, adj = cumulative - start;
      if (adj < 0) { toNext = start - cumulative; }
      else { const rem = adj % stride; toNext = stride - rem; if (toNext === 0) toNext = stride; }
      nextEl.textContent = String(cumulative + toNext);
    }

    // ------- Ledger drawer (opens even if empty) -------
    let drawer; // drawer element cached per section
    function openLedger(sectionRoot){
      if (!drawer) {
        drawer = document.createElement('div');
        drawer.className = 'eyw-ledger';

        const panel = document.createElement('div');
        panel.className = 'eyw-ledger__panel';

        const h = document.createElement('h3');
        h.textContent = 'Payouts Ledger';
        panel.appendChild(h);

        const tbl = document.createElement('table');
        tbl.innerHTML = '<thead><tr><th>Date & Time</th><th>Streak</th><th>Value</th><th>Method</th></tr></thead>';
        const tb = document.createElement('tbody');

        if (Array.isArray(ledgerData) && ledgerData.length) {
          ledgerData.forEach((r) => {
            const tr = document.createElement('tr');
            const dt = new Date(r.at);
            const dateStr = isNaN(dt.getTime()) ? (r.at || '') : dt.toLocaleString();
            const cents = Number(r.value);
            const val = formatMoneyFromCents(cents);
            tr.innerHTML = `<td>${dateStr}</td><td>${r.streak ?? ''}</td><td>${val}</td><td>${r.method ?? ''}</td>`;
            tb.appendChild(tr);
          });
        } else {
          const tr = document.createElement('tr');
          tr.innerHTML = `<td class="eyw-ledger-empty" colspan="4">No payouts yet — come back after your next qualifying streak 🎉</td>`;
          tb.appendChild(tr);
        }

        tbl.appendChild(tb);
        panel.appendChild(tbl);

        const close = document.createElement('button');
        close.textContent = 'Close';
        close.className = 'btn-light eyw-ledger__close';
        close.setAttribute('data-eyw-ledger-close','');
        panel.appendChild(close);

        drawer.appendChild(panel);

        // Close interactions (backdrop, button, ESC)
        drawer.addEventListener('click', (e)=>{ if(e.target === drawer) closeLedger(sectionRoot); });
        close.addEventListener('click', (e)=>{ e.preventDefault(); e.stopPropagation(); closeLedger(sectionRoot); });
        document.addEventListener('keydown', (e)=>{ if (e.key === 'Escape') closeLedger(sectionRoot); });

        document.body.appendChild(drawer);
      }
      drawer.style.display = 'block';
      const closeBtn = drawer.querySelector('[data-eyw-ledger-close]');
      if (closeBtn) { try { closeBtn.focus(); } catch(_) {} }
    }

    function closeLedger(_sectionRoot){
      if (drawer) drawer.style.display = 'none';
    }
  });
})();